import { Label } from "layout/typography/Label";
import { Chips } from "primereact/chips";
import { RadioButton } from "primereact/radiobutton";
import { useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

const Title = styled.h3`
  margin-bottom: 16px;
`;

const Hint = styled.p`
  color: var(--text-color-secondary);
  font-size: 0.875rem;
  margin: 8px 0 16px;
`;

/**
 * CLE-2590 — per-payer group-number rules.
 *
 * Include routes a claim carrying one of these group numbers TO this payer,
 * overriding the payer matched by name. Exclude does the opposite: the claim
 * resolves to no payer at all, which leaves it unpriced and off the
 * underpayment worklist. Group numbers are matched exactly, unlike the member
 * ID prefixes above.
 */
export function GroupNumbers() {
  const { control, watch } = useFormContext();
  const [groupNumbers, setGroupNumbers] = useState<string[]>([]);
  const watchedGroupNumbers = watch("groupNumbers");

  useEffect(() => {
    if (watchedGroupNumbers) {
      setGroupNumbers(watchedGroupNumbers);
    }
  }, [watchedGroupNumbers]);

  return (
    <div className="block bg-white border-round p-4 mb-4">
      <Title>Group Numbers</Title>

      <Controller
        name="groupNumberMode"
        control={control}
        render={({ field: { onChange, value } }) => (
          <div className="flex gap-4 mb-3">
            <div className="flex align-items-center">
              <RadioButton
                inputId="groupNumberMode-include"
                name="groupNumberMode"
                value="INCLUDE"
                checked={value !== "EXCLUDE"}
                onChange={(e) => onChange(e.value)}
              />
              <Label htmlFor="groupNumberMode-include" className="ml-2">
                Include
              </Label>
            </div>
            <div className="flex align-items-center">
              <RadioButton
                inputId="groupNumberMode-exclude"
                name="groupNumberMode"
                value="EXCLUDE"
                checked={value === "EXCLUDE"}
                onChange={(e) => onChange(e.value)}
              />
              <Label htmlFor="groupNumberMode-exclude" className="ml-2">
                Exclude
              </Label>
            </div>
          </div>
        )}
      />

      <Hint>
        Include: claims with these group numbers are mapped to this payer.
        Exclude: claims with these group numbers are not mapped to this payer,
        and are left unpriced. Group numbers must match exactly.
      </Hint>

      <Label htmlFor="groupNumbers">Add Group Numbers</Label>
      <Controller
        name="groupNumbers"
        control={control}
        render={({ field: { onChange } }) => (
          <Chips
            id="groupNumbers"
            value={groupNumbers}
            className="w-full mt-2"
            onChange={(e: any) => {
              const trimmed = (e.value ?? []).map((item: any) =>
                String(item).trim()
              );
              setGroupNumbers(trimmed);
              onChange(trimmed);
            }}
            separator=","
          />
        )}
      />
    </div>
  );
}
